import { ColDef, GridApi, IDetailCellRendererParams, IRowNode } from "ag-grid-community";
import { action, makeObservable, observable } from "mobx";
import ProgressIndicatorModel, {
  IProgressIndicatorModel
} from "../../../../../../components/widgets/ProgressIndicator/ProgressIndicator_model";
import PermissionsContext from "../../../../../../contexts/permissions/PermissionsContext";
import { PermissionFields } from "../../../../../../contexts/permissions/PermissionsTypes";
import { EntityTypes, GridTypes } from "../../../../../../enums";
import { SingleFormModel } from "../../../../../../pages/change/forms/singleFormModel/SingleForm_model";
import ActionTypesApi, {
  ActionTypesApi as IActionTypesApi
} from "../../../../../../services/api/v2/actionTypes/ActionTypes.api";
import AudienceStakeholdersApi, {
  AudienceStakeholdersApi as IAudienceStakeholdersApi
} from "../../../../../../services/api/v2/audenceStakeholders/AudienceStakeholders.api";
import AudiencesApi, { AudiencesApi as IAudiencesApi } from "../../../../../../services/api/v2/audiences/Audiences.api";
import ProjectTeamUserPermissionsApi, {
  ProjectTeamUserPermissionsApi as IProjectTeamUserPermissionsApi
} from "../../../../../../services/api/v2/projectTeamUserPermissions/ProjectTeamUserPermissions.api";
import GridToastService from "../../../../../../services/local/gridToastService/GridToastService";
import { IGridToastService } from "../../../../../../services/local/gridToastService/IGridToastService";
import { IModalContextModel } from "../../../../../modalZ/context/IModalContext";
import ModalContext from "../../../../../modalZ/context/ModalContext";
import ToasterService, { IToasterService } from "../../../../../toaster/ToasterService";
import { GridModalBuilder } from "../../modals/GridModal_builder";
import { AppGridToolbarType } from "../../types/AppGrid_types";
import { IGridUiAction } from "../base/AppGridToolbarActions_view";
import { AppGridState } from "../base/AppGrid_view";
import { ColumnStateAddon } from "../base/addons/ColumnStateAddon";
import { FilterStoreAddon } from "../base/addons/FilterAddon/FilterStoreAddon";
import { TextWrapperAddon } from "../base/addons/TextWrapAddon";
import {
  AuccaColDefFieldNamesEnum,
  CommonColDefFieldNamesEnum,
  StakeholderColDefFieldNamesEnum
} from "../../../enums/AgGridColDefFieldNameEnum";
import { PulseAudienceStakeholdersGridColumnBuilder } from "./PulseAudienceStakeholdersGrid_columns";
import { GetPulseAudienceStakeholdersGridActions } from "./PulseAudienceStakeholdersGrid_actions";

export class PulseAudienceStakeholdersGridModel {
  organisationId: number;
  projectId: number;
  pulseId: number;
  progressStatuses: FP.Generic.IKeyLabel[];
  actionTypesProvider: IActionTypesApi;
  authUser: FP.Entities.IUser;
  filterStoreAddon: FilterStoreAddon;
  columnStateAddon: ColumnStateAddon;
  textWrapAddon: TextWrapperAddon;
  @observable isFilterChanged: boolean;
  @observable isColumnStateChanged: boolean;
  userCanViewStakeholders: boolean;
  userCanEditAudiences: boolean;
  type: GridTypes = GridTypes.IMPACT_ACTION_GRID;
  audiencesGridColumnBuilder: PulseAudienceStakeholdersGridColumnBuilder;
  modalService: IModalContextModel;
  httpProgress: IProgressIndicatorModel;
  @observable confirmationService: IModalContextModel;
  @observable selectedItems: number[] = [];
  toasterService: IToasterService;
  @observable isDuplicatingItems: boolean = false;
  microAudienceForm: SingleFormModel;
  gridApi: GridApi;
  @observable.ref gridActions: IGridUiAction[] = [];
  gridModalBuilder: GridModalBuilder;
  gridToastService: IGridToastService;
  projectTeamUserPermissionsProvider: IProjectTeamUserPermissionsApi = ProjectTeamUserPermissionsApi;
  gridColumns: ColDef<any, any>[];
  detailGridColumns: ColDef<any, any>[];
  detailCellRendererParams: IDetailCellRendererParams<any, any>;
  urlSearchParams: URLSearchParams;
  columnDefs: FP.Entities.IColumnDef[] = [];
  nestedColumnDefs: FP.Entities.IColumnDef[] = [];
  gridToolbarType: AppGridToolbarType;
  audiencesProvider: IAudiencesApi;
  audienceStakeholdersProvider: IAudienceStakeholdersApi;
  gridType: EntityTypes | GridTypes;
  @observable isLoading: boolean = true;
  refreshDataHandler: any;

  constructor(
    organisationId: number,
    projectId: number,
    pulseId: number,
    authUser: FP.Entities.IUser,
    columnDefs: FP.Entities.IColumnDef[],
    urlSearchParams: URLSearchParams,
    gridToolbarType: AppGridToolbarType,
    preSelectedItemIds: number[],
    gridType: EntityTypes | GridTypes,
    refreshDataHandler: any
  ) {
    makeObservable(this);
    this.audienceStakeholdersProvider = AudienceStakeholdersApi;
    this.audiencesProvider = AudiencesApi;
    this.actionTypesProvider = ActionTypesApi;
    this.columnDefs = columnDefs || [];
    this.userCanViewStakeholders = PermissionsContext.canViewField(
      PermissionFields.STAKEHOLDERS,
      organisationId,
      projectId
    );
    this.userCanEditAudiences = PermissionsContext.canEditField(
      PermissionFields.STAKEHOLDERS,
      organisationId,
      projectId
    );
    this.organisationId = organisationId;
    this.projectId = projectId;
    this.pulseId = pulseId;
    this.authUser = authUser;
    this.urlSearchParams = urlSearchParams;
    this.gridToolbarType = gridToolbarType;
    this.selectedItems = preSelectedItemIds;
    this.gridType = gridType;
    this.refreshDataHandler = refreshDataHandler;
    this.modalService = ModalContext;
    this.toasterService = ToasterService;
    this.confirmationService = ModalContext;
    this.httpProgress = ProgressIndicatorModel;
    this.gridToastService = GridToastService;
    this.gridModalBuilder = new GridModalBuilder();
    this.generateAddons();
  }

  @action
  onMount = async () => {
    this.generateFn();
    this.setIsLoading(false);
  };

  @action
  generateFn = () => {
    this.generateGridConfig();
    this.generateActions();
  };

  @action
  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  onUnmount = () => {};

  generateAddons = () => {
    this.textWrapAddon = new TextWrapperAddon(this.projectId, EntityTypes.PULSE_AUDIENCE_STAKEHOLDERS);
    this.filterStoreAddon = new FilterStoreAddon({
      projectId: this.projectId,
      gridType: EntityTypes.PULSE_AUDIENCE_STAKEHOLDERS,
      filterHasChangedFn: this.setIsFilterChanged,
      urlSearchParams: this.urlSearchParams
    });

    this.columnStateAddon = new ColumnStateAddon({
      projectId: this.projectId,
      gridType: EntityTypes.PULSE_AUDIENCE_STAKEHOLDERS,
      columnOrderHasChangedFn: this.setIsColumnStateChanged
    });
  };

  generateGridConfig = () => {
    const canEdit = this.userCanEditAudiences;
    this.audiencesGridColumnBuilder = new PulseAudienceStakeholdersGridColumnBuilder({
      organisationId: this.organisationId,
      projectId: this.projectId,
      canEdit,
      userCanViewStakeholders: this.userCanViewStakeholders,
      onFieldUpdated: this.refreshDataHandler,
      columns: [
        CommonColDefFieldNamesEnum.Name,
        StakeholderColDefFieldNamesEnum.Email,
        AuccaColDefFieldNamesEnum.Awareness,
        AuccaColDefFieldNamesEnum.Understanding,
        AuccaColDefFieldNamesEnum.Commitment,
        AuccaColDefFieldNamesEnum.Capability,
        AuccaColDefFieldNamesEnum.Adoption
      ]
    });
    this.gridColumns = this.audiencesGridColumnBuilder.generateColumnDefs();
  };

  generateActions = () => {
    this.setGridActions(GetPulseAudienceStakeholdersGridActions(this));
  };

  @action
  onFirstDataRendered = (gridApi: GridApi) => {
    const nodesToSelect: IRowNode[] = [];
    gridApi.forEachNode(node => {
      if (!!this.selectedItems?.includes(node.data.id)) {
        nodesToSelect.push(node);
      }
    });
    gridApi.setNodesSelected({ nodes: nodesToSelect, newValue: true });
  };

  @action
  setGridActions = (gridActions: any) => {
    this.gridActions = gridActions;
  };

  @action
  onGridStateUpdate = (gridState: AppGridState) => {
    this.gridApi = gridState.gridApi;
    this.selectedItems = gridState.selectedItems || [];
  };

  @action
  setIsColumnStateChanged = (isColumnStateChanged: boolean) => {
    this.isColumnStateChanged = isColumnStateChanged;
    this.generateActions();
  };

  @action
  setIsFilterChanged = (isFilterChanged: boolean) => {
    this.isFilterChanged = isFilterChanged;
    this.generateActions();
  };
}
